'use client';

import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChakraProvider } from '@chakra-ui/react';
import { CacheProvider } from '@chakra-ui/next-js';
import { datadogRum } from '@datadog/browser-rum';

import { theme } from '@/styles/theme';
import { trpc, createTRPCClient } from '@/trpc';
import { MyOmersTRPCClientError } from '@/server/types';
import { cleanupCookies } from '@/utils/cookies';
import { getRedirectUrl } from '@/utils';

const Providers = ({ children }: { children: React.ReactNode }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            // Keep data fetched from the server cached locally until the page is
            // hard-refreshed
            staleTime: Infinity,
            // When a query fails, retry up to three times. However, if the
            // reason for the failure is because the user's access token has
            // expired, clear the cookie and  redirect them to
            // `/session-timed-out` immediately without retrying.
            // Also don't retry on BAD_REQUEST (validation errors)
            retry(failureCount: number, e: unknown) {
              const error = e as MyOmersTRPCClientError;

              if (error.data?.code === 'UNAUTHORIZED') {
                cleanupCookies();

                datadogRum.clearUser();
                window.location.href = getRedirectUrl('/session-timed-out');
              }

              if (error.data?.code === 'BAD_REQUEST') {
                return false;
              }

              return failureCount <= 3;
            }
          }
        }
      })
  );

  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <CacheProvider>
          <ChakraProvider theme={theme}>{children}</ChakraProvider>
        </CacheProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
};

export default Providers;
